import React, { useState } from 'react';
import { Card, Typography, Button, Space, Dropdown, message } from 'antd';
import { usePurchaseOrderStatusChangeMutation } from 'api/purchaseOrder';
import type { InboundPackage, OutboundPackage } from 'models/Warehouse';
import { StatusBadge } from '../statusBadge';
import { ArrowUpWhiteIcon } from 'components/common/icons/ArrowUpWhiteIcon';
import { ArrowTopRightIcon } from 'components/common/icons/warehouse/ArrowTopRightIcon';
import styles from './styles.module.scss';

const { Text, Title } = Typography;

interface PackageCardProps {
  package: InboundPackage | OutboundPackage;
  onClick: () => void;
  type: 'inbound' | 'outbound';
}

const inboundStatusOptions = [{ key: 'IN_WAREHOUSE', label: 'In Warehouse' }];

export const PackageCard: React.FC<PackageCardProps> = ({ package: pkg, onClick, type }) => {
  const isInbound = type === 'inbound';
  const inboundPkg = pkg as InboundPackage;
  const outboundPkg = pkg as OutboundPackage;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [updateStatus, { isLoading: isUpdating }] = usePurchaseOrderStatusChangeMutation();

  const showChangeStatus = isInbound && inboundPkg.status === 'SHIPPED';

  const handleStatusChange = async (key: string) => {
    try {
      await updateStatus({
        id: inboundPkg.id,
        status: key,
      }).unwrap();
      message.success('Status updated successfully');
      setIsDropdownOpen(false);
    } catch (error) {
      message.error('Failed to update status');
      console.error('Status update error:', error);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click when clicking on buttons or dropdowns
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('.ant-dropdown')) {
      e.stopPropagation();
      return;
    }
    onClick();
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Card className={styles.packageCard} onClick={handleCardClick} hoverable styles={{ body: { padding: '16px' } }}>
      <div className={styles.cardHeader}>
        <div className={styles.orderInfo}>
          <Title level={5} className={styles.orderId}>
            Order: {isInbound ? inboundPkg.uuid : outboundPkg.uuid} <ArrowTopRightIcon />
          </Title>
          <StatusBadge status={pkg.status} type={type} />
        </div>
      </div>

      <div className={styles.cardContent}>
        <div className={styles.infoRow}>
          <Text className={styles.label}>{isInbound ? 'Supplier:' : 'Client:'}</Text>
          <Text className={styles.value} ellipsis>
            {isInbound ? inboundPkg.supplier.name : outboundPkg.client.name}
          </Text>
        </div>

        {isInbound ? (
          <>
            <div className={styles.infoRow}>
              <Text className={styles.label}>Shipment Date:</Text>
              <Text className={styles.value}>{inboundPkg.formattedShipmentDate?.day || '-'}</Text>
            </div>
            <div className={styles.infoRow}>
              <Text className={styles.label}>Expected Arrival Date:</Text>
              <Text className={styles.value}>{inboundPkg.formattedExpectedArrivalDate?.day || '-'}</Text>
            </div>
          </>
        ) : (
          <div className={styles.infoRow}>
            <Text className={styles.label}>Expected Delivery Date:</Text>
            <Text className={styles.value}>{outboundPkg.formattedExpectedDeliveryDate?.day || '-'}</Text>
          </div>
        )}

        {showChangeStatus && (
          <div className={styles.statusChangeWrapper} onClick={handleDropdownClick}>
            <Dropdown
              menu={{
                items: inboundStatusOptions.map((option) => ({
                  key: option.key,
                  label: option.label,
                })),
                onClick: ({ key }) => handleStatusChange(key),
              }}
              trigger={['click']}
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <Button type="primary" block className={styles.changeStatusBtn} loading={isUpdating}>
                <Space>
                  Change Status
                  <span
                    style={{
                      display: 'inline-flex',
                      marginTop: 5,
                      transition: 'transform 0.3s',
                      transform: isDropdownOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                  >
                    <ArrowUpWhiteIcon />
                  </span>
                </Space>
              </Button>
            </Dropdown>
          </div>
        )}
      </div>
    </Card>
  );
};
